class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    FacetFiltersForm.applySortFix();

    if (typeof __hInitRatingSnippet === 'function') {
      __hInitRatingSnippet();
      __hStartRatingCycle();
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static _getPriceSort() {
    let sortBy = new URLSearchParams(window.location.search).get('sort_by');
    if (!sortBy) {
      const sel = document.querySelector('#SortBy');
      if (sel) sortBy = sel.value;
    }
    return sortBy && sortBy.startsWith('price-') ? sortBy : null;
  }

  static _getDisplayPrice(li) {
    const card = li.querySelector('[x-data]');
    if (!card) return 0;
    const m = card.getAttribute('x-data').match(/[^_]price:\s*'([\d.,]+)/);
    if (!m) return 0;
    return parseFloat(m[1].replace('.', '').replace(',', '.'));
  }

  static _sortItems(items, sortBy) {
    const asc = sortBy === 'price-ascending';
    items.sort((a, b) => {
      const d = (typeof a === 'object' && a.price !== undefined)
        ? a.price - b.price
        : FacetFiltersForm._getDisplayPrice(a) - FacetFiltersForm._getDisplayPrice(b);
      return asc ? d : -d;
    });
  }

  static applySortFix() {
    const sortBy = FacetFiltersForm._getPriceSort();
    if (!sortBy) {
      FacetFiltersForm._priceSortData = null;
      return;
    }

    const grid = document.getElementById('product-grid');
    if (!grid) return;

    // 1) Instant per-page sort
    const items = Array.from(grid.querySelectorAll(':scope > li'));
    if (items.length >= 2) {
      FacetFiltersForm._sortItems(items, sortBy);
      items.forEach(i => grid.appendChild(i));
    }

    // 2) Cross-page sort if pagination exists
    let totalPages = 1;
    document.querySelectorAll('.pagination a[href]').forEach(a => {
      const m = a.href.match(/[?&]page=(\d+)/);
      if (m) totalPages = Math.max(totalPages, parseInt(m[1]));
    });
    if (totalPages > 1 && totalPages <= 30) {
      FacetFiltersForm._fetchAndSortAllPages(sortBy, totalPages);
    }
  }

  static async _fetchAndSortAllPages(sortBy, totalPages) {
    if (FacetFiltersForm._sortAbort) FacetFiltersForm._sortAbort.abort();
    const abort = new AbortController();
    FacetFiltersForm._sortAbort = abort;

    const grid = document.getElementById('product-grid');
    const sectionId = grid.dataset.id;
    const perPage = grid.querySelectorAll(':scope > li').length;
    const currentPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1');
    const pathname = window.location.pathname;

    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('sort_by', sortBy);
    const base = params.toString();

    const promises = [];
    for (let p = 1; p <= totalPages; p++) {
      const qs = p > 1 ? `${base}&page=${p}` : base;
      const url = `${pathname}?section_id=${sectionId}&${qs}`;
      promises.push(
        fetch(url, { signal: abort.signal })
          .then(r => r.text())
          .then(html => ({ page: p, html }))
          .catch(() => null)
      );
    }

    const results = (await Promise.all(promises)).filter(Boolean);
    if (abort.signal.aborted) return;

    const allItems = [];
    const pageDocs = {};

    for (const { page, html } of results) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      pageDocs[page] = doc;
      doc.querySelectorAll('#product-grid > li').forEach(li => {
        allItems.push({ html: li.outerHTML, price: FacetFiltersForm._getDisplayPrice(li) });
      });
    }

    FacetFiltersForm._sortItems(allItems, sortBy);
    FacetFiltersForm._priceSortData = { allItems, perPage, totalPages, sortBy, pageDocs };
    // Preload current page images before rendering
    FacetFiltersForm._preloadPageImages(currentPage);
    FacetFiltersForm._renderSortedPage(currentPage);
    FacetFiltersForm._setupPaginationIntercept();
  }

  static _preloadPageImages(page) {
    const data = FacetFiltersForm._priceSortData;
    if (!data) return;
    const start = (page - 1) * data.perPage;
    const pageItems = data.allItems.slice(start, start + data.perPage);
    pageItems.forEach(item => {
      if (!item._preloaded) {
        item._preloaded = true;
        const tmp = document.createElement('div');
        tmp.innerHTML = item.html;
        tmp.querySelectorAll('img[src]').forEach(img => {
          new Image().src = img.getAttribute('src');
        });
      }
    });
  }

  static _renderSortedPage(page) {
    const data = FacetFiltersForm._priceSortData;
    if (!data) return;

    const start = (page - 1) * data.perPage;
    const pageItems = data.allItems.slice(start, start + data.perPage);
    if (!pageItems.length) return;

    const grid = document.getElementById('product-grid');
    if (!grid) return;
    grid.innerHTML = pageItems.map(i => i.html).join('');

    const srcDoc = data.pageDocs[page];
    if (srcDoc) {
      const srcPag = srcDoc.querySelector('.pagination');
      const curPag = document.querySelector('.pagination');
      if (srcPag && curPag) curPag.outerHTML = srcPag.outerHTML;
    }

    grid.querySelectorAll('.scroll-trigger').forEach(el => el.classList.add('scroll-trigger--cancel'));
    if (typeof __hInitRatingSnippet === 'function') {
      __hInitRatingSnippet();
      __hStartRatingCycle();
    }

    // Preload adjacent pages
    const totalPages = Math.ceil(data.allItems.length / data.perPage);
    if (page > 1) FacetFiltersForm._preloadPageImages(page - 1);
    if (page < totalPages) FacetFiltersForm._preloadPageImages(page + 1);
  }

  static _setupPaginationIntercept() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    if (container._priceSortHandler) container.removeEventListener('click', container._priceSortHandler);

    container._priceSortHandler = (e) => {
      if (!FacetFiltersForm._priceSortData) return;
      const link = e.target.closest('.pagination a[href]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      const m = link.href.match(/[?&]page=(\d+)/);
      const page = m ? parseInt(m[1]) : 1;

      FacetFiltersForm._renderSortedPage(page);

      const params = new URLSearchParams(window.location.search);
      if (page > 1) params.set('page', page.toString());
      else params.delete('page');
      history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);

      document.getElementById('ProductGridContainer').scrollIntoView({ behavior: 'smooth' });
    };

    container.addEventListener('click', container._priceSortHandler);
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    const forms = [];

    sortFilterForms.forEach((form) => {
      if (
        form.id === 'FacetSortForm' ||
        form.id === 'FacetFiltersForm' ||
        form.id === 'FacetSortDrawerForm' ||
        form.id === 'FacetFiltersFormMobile'
      ) {
        const noJsElements = document.querySelectorAll('.no-js-list');
        noJsElements.forEach((el) => el.remove());
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.applySortFix();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// The mobile filter drawer starts below the sticky bars at the top of the page.
// Their combined height is not constant: at the top of the page the announcement
// bar and the header are both visible, but once the announcement bar has scrolled
// away only the header remains. Publishing the header's live bottom edge as
// --facets-drawer-offset keeps the drawer (and its close button, which sits in the
// <summary> outside the form) flush against the nav in both states.
(() => {
  const header = document.querySelector('.section-header');
  if (!header) return;

  const setOffset = () => {
    const bottom = Math.max(0, header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--facets-drawer-offset', `${bottom}px`);
  };

  setOffset();
  window.addEventListener('scroll', setOffset, { passive: true });
  window.addEventListener('resize', setOffset, { passive: true });
})();
